/**
 * GatewayVlans -- Gateway > Configure > Site VLANs: the SITE VLAN table. Also served at
 * configure/vlans as the "VLANs" tab of Customer > Site services, where it renders the Site
 * services tab strip (SiteTabs decides from the path; OP#1858).
 *
 * Owner request (2026-09-14): "almost the same as Meraki" (Security & SD-WAN > Addressing &
 * VLANs): ONE network-level table -- ID, VLAN name, Subnet, VLAN interface IP, Group policy --
 * with Add VLAN and Delete. VLANs belong to the SITE, not to one gateway, and every site VLAN is
 * applied to all gateways and all switches of the site.
 *
 * Owner feedback on the first version (2026-09-14) that shapes this one:
 *   (1) "always deploy new VLAN without asking and delete any VLAN that is removed from the
 *       site" -> saving or deleting a VLAN queues an AUTOMATIC deploy server-side
 *       (services/site_vlan_auto_deploy). The page shows that deploy live (DeployStrip) instead
 *       of a "Review & apply" banner; "Retry now" appears only after a failed/refused deploy,
 *       with the reason in plain words. The review dialog survives as "Deploy plan" (details,
 *       verify, and the manual path when auto-deploy is off). "Dry" VLANs typed straight onto a
 *       port or an SSID are untouched by all of this: they are never site VLANs, so they are
 *       never removed.
 *   (2) "Not applied / Partial · GW 0/2 · SW 0/2 -- not sure what it means" -> the Applied column
 *       is one status word whose count matches the device names under it (lib/siteVlanApplied).
 *       Deleted VLANs still on a device show as "Removing" rows. The LAN row says "Gateway LAN --
 *       not changed by VLAN deploys".
 *   (3) "we should be able to see the current IP even if we cannot change it" -> the locked WAN
 *       card shows the live address / gateway / DNS from the gateway's state document.
 *
 *   1. STRIP     auto-deploy state: queued / running (per-device steps) / refused / failed /
 *                deployed, the on/off toggle (msp_admin) and Retry now.
 *   2. TABLE     site VLANs + read-only context rows (untagged LAN, tagged VLANs a gateway carries
 *                that the site list does not) + deleted VLANs still being removed.
 *   3. GATEWAY   per box: the locked WAN card (live address), the live interface list, then the HA
 *                pair and AP management VLAN settings (components/site/SiteVlanRollout).
 *
 * Group policy reads "None" on every row, on purpose: the whitebox firmware carries no policy
 * object; the column stays so the shape matches the MX an operator compares us against.
 *
 * Design system: surface/ink tokens for structure, the status ramp only for operational state
 * (live / deploying / missing / can't deploy), brand violet only for interactive affordances (the
 * toggle, buttons, focus); light + dark via tokens; tablet-first (the strip wraps, tables scroll
 * inside their card, 44px touch targets).
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useQueryClient } from "@tanstack/react-query";
import { useSession } from "@/stores/session";
import { useSearchParams } from "react-router-dom";
import { useDevices, useGatewayNetworksMany } from "@/lib/api/hooks";
import { qk } from "@/lib/api/queryKeys";
import { ApiError } from "@/lib/api/client";
import {
  useAutoDeploy,
  useDeleteSiteVlan,
  useDropRemoval,
  useGatewayHaPair,
  useRetryAutoDeploy,
  useSetAutoDeploy,
  useSiteVlans,
  useVlanRolloutPlan,
  type AutoDeployView,
  type HaPairView,
  type PendingRemoval,
  type RolloutDeviceResult,
  type SiteVlanRow,
} from "@/lib/api/siteVlans";
import {
  deployHeadline,
  deviceCountPhrase,
  deviceStatusWord,
  dhcpSummary,
  expectedGatewayAddress,
  gatewayOnlyNetworks,
  gatewayRole,
  ipv4ToInt,
  liveDhcpLine,
  parseCidr,
  siteGatewayAddress,
  summarizeRemoval,
  summarizeVlanApplied,
  timeAgo,
  wanAddressLine,
  type DeviceApplied,
  type GatewayLiveNetwork,
  type GatewayRole,
  type RemovalSummary,
  type RowApplied,
} from "@/lib/siteVlanApplied";
import type { GatewayNetworkRow, GatewayNetworks, HealthState } from "@/types/domain";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { VlanPathButton } from "@/components/site/VlanPathCheck";
import { SiteTabs } from "@/components/site/SiteTabs";
import { StatusPill } from "@/components/ui/StatusPill";
import { RoleGate } from "@/components/auth/RoleGate";
import {
  GatewayPicker,
  INPUT,
  LockedWanCard,
  NotEditableBanner,
} from "@/components/gateway/GatewayChrome";
import { SiteReassertCard } from "@/components/gateway/ReassertManaged";
import { StateReportingNotice } from "@/components/gateway/EnableStateReporting";
import {
  ApNativeCard,
  HaPairCard,
  PendingSwitchesCard,
  RolloutPanel,
  SiteVlanDialog,
  WRITE_REASON,
  WRITE_ROLE,
} from "@/components/site/SiteVlanRollout";
import { cn } from "@/lib/cn";

/** Table column count (checkbox, ID, name, subnet, interface IP, group policy, applied, actions). */
const COLS = 8;

export default function GatewayVlans() {
  const siteId = useSession((s) => s.currentSiteId);
  const siteName = useSession((s) => s.currentSite()?.name);

  // Site VLANs belong to ONE site: without a site in scope there is no table to show.
  if (!siteId) {
    return (
      <div>
        <PageHeader title="Site VLANs" subtitle="Site VLANs, deployed to every gateway and switch" />
        <EmptyState
          title="Select a site"
          hint="VLANs belong to a site and deploy to every gateway and switch at it. Pick a site from the scope bar."
        />
      </div>
    );
  }
  // Keyed by site: switching sites must not carry a selection, an open dialog or an expanded row.
  return <SiteVlanPage key={siteId} siteId={siteId} siteName={siteName ?? null} />;
}

/** A clock for relative times ("3 min ago") that re-renders every `intervalMs`. */
function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return now;
}

/** An error's operator text (ApiError carries the server's `error` message). */
function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/* =========================================================================================
 * Page
 * ========================================================================================= */

function SiteVlanPage({ siteId, siteName }: { siteId: string; siteName: string | null }) {
  const orgId = useSession((s) => s.currentOrgId);
  const qc = useQueryClient();
  const now = useNow(30_000);

  // --- site-level data ---------------------------------------------------------------------
  const vlans = useSiteVlans(siteId);
  const haQuery = useGatewayHaPair(siteId);
  const haPair: HaPairView | null = haQuery.data?.ha_pair ?? null;
  const rows = useMemo(
    () => [...(vlans.data?.items ?? [])].sort((a, b) => a.vlan_id - b.vlan_id),
    [vlans.data],
  );

  // Auto-deploy: state, the newest run (any trigger) and the deleted VLANs still to remove.
  const autoQuery = useAutoDeploy(siteId);
  const autoView: AutoDeployView | null = autoQuery.data?.auto_deploy ?? null;
  const rollout = autoView?.last_run ?? null;
  const removals: PendingRemoval[] = useMemo(() => autoView?.pending_removals ?? [], [autoView]);

  // --- devices at the site (useDevices is scoped to the current site) ----------------------
  const gwList = useDevices({ kind: "gateway", limit: 100 });
  const swList = useDevices({ kind: "switch", limit: 100 });
  const gateways = useMemo(() => orderGateways(gwList.data?.items ?? [], haPair), [gwList.data, haPair]);
  const switches = useMemo(
    () => (swList.data?.items ?? []).map((d) => ({ serial: d.serial, name: d.name || d.serial })),
    [swList.data],
  );

  // Every site gateway's live networks: the gateway half of the Applied column + the detail card.
  const netQueries = useGatewayNetworksMany(gateways.map((g) => g.serial));
  // useQueries hands back a new array every render; key the derived table list on when each
  // gateway's data last changed so the memos below recompute only on real data changes.
  const netStamp = netQueries.map((q) => q.dataUpdatedAt).join("|");
  const tables = useMemo(
    () => gateways.map((g, i) => ({ ...g, table: netQueries[i]?.data })),
    // netQueries is read through netStamp (its data timestamps), not by array identity.
    [gateways, netStamp],
  );
  const netBySerial: Record<string, { data?: GatewayNetworks; isLoading: boolean; error: unknown }> = {};
  gateways.forEach((g, i) => {
    const q = netQueries[i];
    netBySerial[g.serial] = { data: q?.data, isLoading: !!q?.isLoading, error: q?.error ?? null };
  });

  // The READ-ONLY plan (owgw GETs only, ~0.4 s on Jedi Home) is the switch half of the column.
  // Same cache entry the plan dialog uses, so opening it starts from it and refreshes.
  const planEnabled = (rows.length > 0 || removals.length > 0) && gateways.length + switches.length > 0;
  const plan = useVlanRolloutPlan(siteId, planEnabled);

  // When a deploy finishes (running -> anything else, or a newer run appears), what the devices
  // carry has changed: re-read the plan, every gateway's config and the manual status view.
  const lastSeen = useRef<{ state?: string; runId?: number }>({});
  useEffect(() => {
    const prev = lastSeen.current;
    const state = autoView?.state;
    const runId = autoView?.last_run?.id;
    const finished =
      (prev.state === "running" && state !== "running") ||
      (prev.runId != null && runId != null && runId !== prev.runId && state !== "running");
    if (finished) {
      void qc.invalidateQueries({ queryKey: qk.sites.vlanRolloutPlan(orgId, siteId) });
      void qc.invalidateQueries({ queryKey: qk.sites.vlanRolloutStatus(orgId, siteId) });
      for (const g of gateways) {
        void qc.invalidateQueries({ queryKey: qk.devices.gatewayNetworks(orgId, g.serial) });
      }
    }
    lastSeen.current = { state, runId };
  }, [autoView?.state, autoView?.last_run?.id, qc, orgId, siteId, gateways]);

  const deploy = autoView ? { state: autoView.state, enabled: autoView.enabled } : null;
  const summaries = useMemo(() => {
    const out = new Map<number, RowApplied>();
    for (const r of rows) {
      out.set(
        r.vlan_id,
        summarizeVlanApplied({
          vlan: r,
          haPair,
          gateways: tables,
          switches,
          switchPlans: plan.data?.switches ?? null,
          rollout,
          deploy,
        }),
      );
    }
    return out;
    // `deploy` is derived from autoView's state/enabled, listed explicitly.
  }, [rows, haPair, tables, switches, plan.data, rollout, autoView?.state, autoView?.enabled]);

  const removalSummaries = useMemo(() => {
    const out = new Map<number, RemovalSummary>();
    for (const r of removals) {
      out.set(
        r.vlan_id,
        summarizeRemoval({
          vlanId: r.vlan_id,
          gateways: tables,
          switches,
          switchPlans: plan.data?.switches ?? null,
          deploy: autoView,
        }),
      );
    }
    return out;
  }, [removals, tables, switches, plan.data, autoView]);

  const context = useMemo(
    () => gatewayOnlyNetworks(tables, rows.map((r) => r.vlan_id), removals.map((r) => r.vlan_id)),
    [rows, tables, removals],
  );

  // --- local UI state ----------------------------------------------------------------------
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<number[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [editing, setEditing] = useState<SiteVlanRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  // One-switch re-plan of a switch skipped while offline (owner decision 2026-10-13). Opened from
  // the Pending VLAN changes card, or from the switch's device sheet via ?replan=<serial>.
  const [searchParams, setSearchParams] = useSearchParams();
  const [replanSerial, setReplanSerial] = useState<string | null>(() => searchParams.get("replan"));
  const closeReplan = () => {
    setReplanSerial(null);
    if (searchParams.has("replan")) {
      const next = new URLSearchParams(searchParams);
      next.delete("replan");
      setSearchParams(next, { replace: true });
    }
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.vlan_id, r.vlan_name, r.subnet, r.gateway_ip].some((v) => String(v ?? "").toLowerCase().includes(q)),
    );
  }, [rows, query]);

  const hasDevices = gateways.length + switches.length > 0;

  return (
    <div>
      <PageHeader
        title="Site VLANs"
        subtitle={`${siteName ?? "This site"} — one VLAN table, deployed automatically to every gateway and switch at the site`}
        actions={
          <>
            <Button
              variant="secondary"
              disabled={(rows.length === 0 && removals.length === 0) || !hasDevices}
              onClick={() => setReviewOpen(true)}
            >
              Deploy plan
            </Button>
            <RoleGate min={WRITE_ROLE} mode="disable" reason={WRITE_REASON}>
              <Button variant="primary" onClick={() => setAdding(true)}>
                Add VLAN
              </Button>
            </RoleGate>
          </>
        }
      />
      {/* Site services tab strip -- renders only at configure/vlans, not at gateway-vlans. */}
      <SiteTabs />

      <div className="flex flex-col gap-4">
        {vlans.error && <ErrorBanner error={vlans.error} />}
        {gwList.error && <ErrorBanner error={gwList.error} />}

        {/* ---- 1. auto-deploy ---------------------------------------------------------------- */}
        {hasDevices && (rows.length > 0 || removals.length > 0 || !!autoView?.last_run) && (
          <DeployStrip
            siteId={siteId}
            view={autoView}
            loading={autoQuery.isLoading}
            error={autoQuery.error}
            counts={{ gateways: gateways.length, switches: switches.length }}
            now={now}
            onReview={() => setReviewOpen(true)}
          />
        )}

        {/* Switches skipped while offline whose VLANs are still pending (renders nothing if none). */}
        {hasDevices && <PendingSwitchesCard siteId={siteId} onReplan={setReplanSerial} />}

        {/* ---- 2. the site VLAN table (Meraki columns + Applied) ------------------------- */}
        <section className="md-card overflow-hidden" aria-label="Site VLANs">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-3 py-2">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search VLANs"
              aria-label="Search VLANs"
              className={`${INPUT} min-h-touch w-64 max-w-full text-sm`}
            />
            <div className="flex items-center gap-2">
              {selected.length > 0 && (
                <span className="text-sm text-ink-muted">{selected.length} selected</span>
              )}
              <RoleGate min={WRITE_ROLE} mode="hide">
                <Button variant="danger" disabled={selected.length === 0} onClick={() => setDeleting(true)}>
                  Delete
                </Button>
              </RoleGate>
            </div>
          </div>

          {vlans.isLoading && <p className="px-4 py-3 text-sm text-ink-muted">Loading VLANs…</p>}

          <div className="overflow-x-auto">
            <table className="w-full min-w-[56rem] text-left text-sm">
              <thead className="text-xs text-ink-subtle">
                <tr className="border-b border-line [&>th]:px-3 [&>th]:py-2 [&>th]:font-medium">
                  <th className="w-10">
                    <RoleGate min={WRITE_ROLE} mode="hide">
                      <input
                        type="checkbox"
                        aria-label="Select all site VLANs"
                        checked={visible.length > 0 && visible.every((r) => selected.includes(r.vlan_id))}
                        onChange={(e) => setSelected(e.target.checked ? visible.map((r) => r.vlan_id) : [])}
                      />
                    </RoleGate>
                  </th>
                  <th className="w-24">ID</th>
                  <th>VLAN name</th>
                  <th>Subnet</th>
                  <th>VLAN interface IP</th>
                  <th>Group policy</th>
                  <th className="min-w-[14rem]">On devices</th>
                  <th className="w-20">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Read-only context from the gateways' live config: the untagged LAN. */}
                {!query &&
                  context.untagged.map((n) => (
                    <LiveContextRow key={`u-${n.name}-${n.subnet}`} network={n} haPair={haPair} kind="untagged" />
                  ))}

                {visible.map((r) => {
                  const summary = summaries.get(r.vlan_id);
                  const open = expanded === r.vlan_id;
                  return (
                    <Fragment key={r.vlan_id}>
                      <tr className="border-t border-line/60 align-top [&>td]:px-3 [&>td]:py-2 hover:bg-surface-2/60">
                        <td>
                          <RoleGate min={WRITE_ROLE} mode="hide">
                            <input
                              type="checkbox"
                              aria-label={`Select VLAN ${r.vlan_id}`}
                              checked={selected.includes(r.vlan_id)}
                              onChange={() =>
                                setSelected((cur) =>
                                  cur.includes(r.vlan_id) ? cur.filter((v) => v !== r.vlan_id) : [...cur, r.vlan_id],
                                )
                              }
                            />
                          </RoleGate>
                        </td>
                        <td className="font-mono tabular-nums text-ink">{r.vlan_id}</td>
                        <td className="font-medium text-ink">{r.vlan_name}</td>
                        <td className="font-mono text-ink-muted">
                          {r.subnet ?? <span className="font-sans text-ink-subtle">Layer 2 only</span>}
                          <div className="font-sans text-[0.6875rem] text-ink-subtle">{dhcpSummary(r, haPair)}</div>
                        </td>
                        <td className="font-mono text-ink-muted">
                          {siteGatewayAddress(r, haPair) ?? "—"}
                          <PhysicalAddresses vlan={r} haPair={haPair} gateways={gateways} />
                        </td>
                        {/* Always None: this firmware has no policy object. See the header. */}
                        <td className="text-ink-subtle">None</td>
                        <td>
                          {summary ? (
                            <AppliedCell
                              summary={summary}
                              open={open}
                              controls={`vlan-${r.vlan_id}-devices`}
                              onToggle={() => setExpanded(open ? null : r.vlan_id)}
                            />
                          ) : (
                            <span className="text-ink-subtle">—</span>
                          )}
                        </td>
                        <td className="text-right">
                          <div className="flex flex-wrap justify-end gap-1">
                            {/* OP#1818: read-only, so every role that sees the site may trace it. */}
                            <VlanPathButton siteId={siteId} vlan={r.vlan_id} variant="ghost" />
                            <RoleGate min={WRITE_ROLE} mode="hide">
                              <Button variant="ghost" onClick={() => setEditing(r)}>
                                Edit
                              </Button>
                            </RoleGate>
                          </div>
                        </td>
                      </tr>
                      {open && summary && (
                        <tr id={`vlan-${r.vlan_id}-devices`} className="bg-surface-2/50">
                          <td colSpan={COLS} className="px-3 pb-3 pt-1">
                            <DeviceBreakdown
                              devices={summary.devices}
                              planError={plan.error}
                              planLoading={plan.isLoading}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}

                {/* Deleted site VLANs a device still carries: removed by the next deploy. */}
                {!query &&
                  removals.map((r) => (
                    <RemovalRow
                      key={`r-${r.vlan_id}`}
                      siteId={siteId}
                      removal={r}
                      summary={removalSummaries.get(r.vlan_id) ?? null}
                    />
                  ))}

                {/* Tagged VLANs a gateway carries that were never in the site list. */}
                {!query &&
                  context.unmanaged.map((n) => (
                    <LiveContextRow key={`m-${n.vlan}-${n.name}`} network={n} haPair={haPair} kind="unmanaged" />
                  ))}
              </tbody>
            </table>
          </div>

          {vlans.data && rows.length === 0 && removals.length === 0 && (
            <p className="border-t border-line px-4 py-6 text-center text-sm text-ink-muted">
              No site VLANs yet. Add one and it deploys to every gateway and switch at the site.
            </p>
          )}
          <p className="border-t border-line px-3 py-2 text-[0.6875rem] text-ink-subtle">
            {deviceCountPhrase(gateways.length, switches.length)} at this site
            {haPair ? " · HA pair: clients use the VIP, each gateway holds its own physical address" : ""}
            {plan.isFetching ? " · reading switch state…" : ""}. Saving a VLAN deploys it to every gateway and switch;
            deleting one removes it from them. VLANs set directly on a port or an SSID are not touched.
          </p>
        </section>

        {/* ---- 3. per-gateway: WAN + live interfaces, then the HA pair and AP VLAN ---------- */}
        <GatewayLiveSection gateways={gateways} haPair={haPair} networks={netBySerial} now={now} />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <HaPairCard siteId={siteId} />
          <ApNativeCard siteId={siteId} />
        </div>
        {/* Site bulk "Re-apply managed settings": every gateway, sequentially, HA backup first. */}
        {gateways.length > 0 && <SiteReassertCard siteId={siteId} />}
      </div>

      {(adding || editing) && (
        <SiteVlanDialog
          siteId={siteId}
          row={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
        />
      )}

      {deleting && (
        <DeleteDialog
          siteId={siteId}
          vlanIds={selected}
          rows={rows}
          summaries={summaries}
          onClose={() => setDeleting(false)}
          onDeleted={() => {
            setDeleting(false);
            setSelected([]);
          }}
        />
      )}

      {reviewOpen && <ReviewDialog siteId={siteId} onClose={() => setReviewOpen(false)} />}
      {replanSerial && <ReviewDialog siteId={siteId} scopeSwitch={replanSerial} onClose={closeReplan} />}
    </div>
  );
}

/** HA primary first, secondary second, then any other gateway by name. */
function orderGateways(
  devices: { serial: string; name: string }[],
  haPair: HaPairView | null,
): { serial: string; name: string }[] {
  const rank = (serial: string) =>
    haPair?.primary?.serial === serial ? 0 : haPair?.secondary?.serial === serial ? 1 : 2;
  return devices
    .map((d) => ({ serial: d.serial, name: d.name || d.serial }))
    .sort((a, b) => rank(a.serial) - rank(b.serial) || a.name.localeCompare(b.name));
}

const ROLE_LABEL: Record<GatewayRole, string> = {
  primary: "HA primary",
  secondary: "HA secondary",
  single: "single gateway",
};

/* =========================================================================================
 * Auto-deploy strip
 * ========================================================================================= */

/** The leading state mark: status-ramp colour, pulsing while a deploy is queued or running. */
const MARK: Record<HealthState | "info", string> = {
  healthy: "bg-healthy",
  warn: "bg-warn",
  critical: "bg-critical",
  unknown: "bg-ink-subtle",
  info: "bg-info",
};

/** One device's step in a running deploy. */
const STEP: Record<RolloutDeviceResult["status"], { tone: HealthState | "info"; label: string }> = {
  pending: { tone: "unknown", label: "Waiting" },
  running: { tone: "info", label: "Deploying" },
  applied: { tone: "healthy", label: "Done" },
  failed: { tone: "critical", label: "Failed" },
  skipped: { tone: "unknown", label: "Not attempted" },
  skipped_offline: { tone: "warn", label: "Skipped (offline)" },
  held_pending_replan: { tone: "warn", label: "Held for re-plan" },
};

function DeployStrip({
  siteId,
  view,
  loading,
  error,
  counts,
  now,
  onReview,
}: {
  siteId: string;
  view: AutoDeployView | null;
  loading: boolean;
  error: unknown;
  counts: { gateways: number; switches: number };
  now: number;
  onReview: () => void;
}) {
  const setAuto = useSetAutoDeploy(siteId);
  const retry = useRetryAutoDeploy(siteId);
  const keep = useDropRemoval(siteId);
  const headline = deployHeadline(view, counts, now);
  const enabled = view?.enabled ?? true;
  const busy = view?.state === "queued" || view?.state === "running";
  const tone = headline?.tone ?? "unknown";
  const title = headline?.title ?? (loading ? "Reading deploy state…" : "Auto-deploy is on");
  const body =
    headline?.body ??
    (headline
      ? null
      : `Saved VLAN changes go to ${deviceCountPhrase(counts.gateways, counts.switches)} automatically; deleted VLANs are removed from them.`);
  const run = view?.last_run;
  const writeError = setAuto.error ?? retry.error ?? keep.error;

  return (
    <section
      aria-label="VLAN auto-deploy"
      className={cn(
        "md-card flex flex-col gap-3 p-4 text-sm",
        tone === "critical" && "border-critical/40 bg-critical-soft",
        tone === "info" && "border-info/30 bg-info-soft",
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3" role="status" aria-live="polite">
          <span
            aria-hidden
            className={cn("mt-1.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full", MARK[tone], busy && "animate-pulse")}
          />
          <div className="min-w-0">
            <div className="font-medium text-ink">{title}</div>
            {body && <div className="mt-0.5 break-words text-ink-muted">{body}</div>}
            {headline && headline.reasons.length > 0 && (
              <ul className="mt-2 flex flex-col gap-1">
                {headline.reasons.map((reason, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-ink">
                    <span aria-hidden className="text-critical">
                      ✕
                    </span>
                    <span className="min-w-0 flex-1 break-words">{reason.text}</span>
                    {/* A deleted VLAN that is used again: keeping it on the devices unblocks the deploy. */}
                    {reason.keepVlan != null && (
                      <RoleGate min={WRITE_ROLE} mode="hide">
                        <Button
                          variant="secondary"
                          disabled={keep.isPending}
                          title="Stop removing this VLAN: the devices keep it and Meridian forgets it"
                          onClick={() => keep.mutate(reason.keepVlan!)}
                        >
                          Keep VLAN {reason.keepVlan} on devices
                        </Button>
                      </RoleGate>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {headline?.canRetry && enabled && (
            <RoleGate min={WRITE_ROLE} mode="hide">
              <Button variant="primary" disabled={retry.isPending} onClick={() => retry.mutate()}>
                {retry.isPending ? "Queuing…" : "Retry now"}
              </Button>
            </RoleGate>
          )}
          {(headline?.canRetry || !enabled) && (
            <Button variant={enabled ? "ghost" : "secondary"} onClick={onReview}>
              {enabled ? "Details" : "Review & apply"}
            </Button>
          )}
          <RoleGate min={WRITE_ROLE} mode="disable" reason={WRITE_REASON}>
            <AutoDeployToggle
              checked={enabled}
              pending={setAuto.isPending || loading}
              onChange={(value) => setAuto.mutate(value)}
            />
          </RoleGate>
        </div>
      </div>

      {view?.state === "running" && run && run.devices.length > 0 && (
        <ol className="flex flex-wrap gap-1.5" aria-label="Deploy progress">
          {run.devices.map((d, i) => {
            const step = STEP[d.status] ?? STEP.pending;
            return (
              <li
                key={d.serial}
                className="flex min-h-touch items-center gap-2 rounded border border-line bg-surface px-2.5 text-xs"
              >
                <span className="tabular-nums text-ink-subtle">{i + 1}</span>
                <span className="text-ink">{d.name}</span>
                <StatusPill status={step.tone}>{d.no_op && d.status === "applied" ? "No change" : step.label}</StatusPill>
              </li>
            );
          })}
        </ol>
      )}
      {error != null && <ErrorBanner error={error} />}
      {writeError != null && <ErrorBanner error={writeError} />}
    </section>
  );
}

/** On/off switch for the site's auto-deploy. `disabled` is set by RoleGate for non-admins. */
function AutoDeployToggle({
  checked,
  pending,
  onChange,
  disabled,
}: {
  checked: boolean;
  pending: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled || pending}
      title={
        checked
          ? "Turn off: saved VLAN changes will wait for a manual apply"
          : "Turn on: deploys any saved VLAN changes"
      }
      onClick={() => onChange(!checked)}
      className="flex min-h-touch items-center gap-2 rounded px-2 text-sm text-ink hover:bg-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span
        aria-hidden
        className={cn(
          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors",
          checked ? "border-brand bg-brand" : "border-line bg-surface-2",
        )}
      >
        <span
          className={cn(
            "absolute h-3.5 w-3.5 rounded-full bg-surface shadow-sm transition-transform",
            checked ? "translate-x-[1.125rem]" : "translate-x-0.5",
          )}
        />
      </span>
      Auto-deploy
    </button>
  );
}

/* =========================================================================================
 * Table cells
 * ========================================================================================= */

/** Under the VIP on an HA pair: which physical address each box holds ("JH-GW-01 .2"). */
function PhysicalAddresses({
  vlan,
  haPair,
  gateways,
}: {
  vlan: SiteVlanRow;
  haPair: HaPairView | null;
  gateways: { serial: string; name: string }[];
}) {
  if (!haPair || !vlan.subnet) return null;
  const parts = gateways
    .filter((g) => gatewayRole(g.serial, haPair) !== "single")
    .map((g) => {
      const ip = expectedGatewayAddress(vlan, gatewayRole(g.serial, haPair), haPair);
      return `${g.name} .${ip?.split(".").pop() ?? "?"}`;
    });
  if (parts.length === 0) return null;
  return <div className="font-sans text-[0.6875rem] text-ink-subtle">VIP · {parts.join(" · ")}</div>;
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg aria-hidden viewBox="0 0 12 12" className={cn("h-3 w-3 text-ink-subtle transition-transform", open && "rotate-180")}>
      <path d="M3 4.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/**
 * The "On devices" cell: ONE status word that matches the names under it ("Missing on 2 devices"
 * + "Missing on JH-GW-02, ECS4150-28P"). Tap = per-device detail.
 */
function AppliedCell({
  summary,
  open,
  controls,
  onToggle,
}: {
  summary: RowApplied;
  open: boolean;
  controls: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={controls}
      onClick={onToggle}
      className="-mx-1 flex min-h-touch w-full max-w-[20rem] flex-col items-start justify-center gap-0.5 rounded px-1 py-1 text-left hover:bg-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
    >
      <span className="flex items-center gap-1.5">
        <StatusPill status={summary.tone}>{summary.label}</StatusPill>
        <Chevron open={open} />
      </span>
      {summary.detail && <span className="text-[0.6875rem] leading-snug text-ink-muted">{summary.detail}</span>}
    </button>
  );
}

/** The expanded row: every device and what it holds for this VLAN, in plain words. */
function DeviceBreakdown({
  devices,
  planError,
  planLoading,
}: {
  devices: DeviceApplied[];
  planError: unknown;
  planLoading: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <ul className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
        {devices.map((d) => {
          const word = deviceStatusWord(d);
          return (
            <li
              key={d.serial}
              className="flex items-start gap-2 rounded border border-line bg-surface px-2.5 py-1.5 text-xs"
            >
              <StatusPill status={word.tone} className="shrink-0">
                {word.label}
              </StatusPill>
              <span className="min-w-0">
                <span className="font-medium text-ink">{d.name}</span>{" "}
                <span className="text-ink-subtle">
                  {d.kind === "gateway" ? `gateway · ${ROLE_LABEL[d.role ?? "single"]}` : "switch"}
                </span>
                <span className="block break-words text-ink-muted">{d.detail}</span>
              </span>
            </li>
          );
        })}
      </ul>
      {planLoading && <p className="text-[0.6875rem] text-ink-subtle">Reading switch state…</p>}
      {planError != null && (
        <p className="text-[0.6875rem] text-ink-subtle">
          Switch state could not be read ({errorText(planError)}); open the deploy plan for details.
        </p>
      )}
    </div>
  );
}

/** A deleted site VLAN still pending removal: where it still is, and "Keep on devices". */
function RemovalRow({
  siteId,
  removal,
  summary,
}: {
  siteId: string;
  removal: PendingRemoval;
  summary: RemovalSummary | null;
}) {
  const drop = useDropRemoval(siteId);
  return (
    <tr className="border-t border-line/60 align-top text-ink-muted [&>td]:px-3 [&>td]:py-2">
      <td />
      <td className="font-mono tabular-nums line-through">{removal.vlan_id}</td>
      <td>
        <span className="text-ink">{removal.vlan_name}</span>
        <div className="text-[0.6875rem] text-ink-subtle">
          Deleted from the site{removal.deleted_by ? ` by ${removal.deleted_by}` : ""}
        </div>
      </td>
      <td className="font-mono">{removal.subnet ?? <span className="font-sans text-ink-subtle">Layer 2 only</span>}</td>
      <td className="text-ink-subtle">—</td>
      <td className="text-ink-subtle">—</td>
      <td>
        {summary ? (
          <div className="flex flex-col items-start gap-0.5">
            <StatusPill status={summary.tone}>{summary.label}</StatusPill>
            <span className="text-[0.6875rem] leading-snug text-ink-muted">{summary.detail}</span>
          </div>
        ) : (
          <span className="text-ink-subtle">—</span>
        )}
        {drop.error != null && <div className="text-[0.6875rem] text-critical">{errorText(drop.error)}</div>}
      </td>
      <td className="text-right">
        <RoleGate min={WRITE_ROLE} mode="hide">
          <Button
            variant="ghost"
            disabled={drop.isPending}
            title="Stop removing this VLAN: the devices keep it and Meridian forgets it"
            onClick={() => drop.mutate(removal.vlan_id)}
          >
            Keep on devices
          </Button>
        </RoleGate>
      </td>
    </tr>
  );
}

/**
 * A network the gateways carry that is NOT a site VLAN row: the untagged LAN (context, as the
 * per-gateway page always showed it) or a tagged VLAN that was never in the site list. Read-only.
 */
function LiveContextRow({
  network,
  haPair,
  kind,
}: {
  network: GatewayLiveNetwork;
  haPair: HaPairView | null;
  kind: "untagged" | "unmanaged";
}) {
  const ips = Array.from(new Set(network.addresses.map((a) => a.ip ?? "—")));
  // HA pair + untagged LAN still on the VIP host on a box: the HA agent refuses to render until
  // the LAN is re-addressed (plan warning ha_lan_on_vip). Worth saying here, not only in review.
  const vipHost = haPair?.addressing?.vip_host ?? 1;
  const net = parseCidr(network.subnet);
  const onVip =
    kind === "untagged" &&
    !!haPair &&
    !!net &&
    network.addresses.some((a) => {
      const n = ipv4ToInt(a.ip);
      return n != null && n - net.network === vipHost;
    });
  return (
    <tr className="border-t border-line/60 align-top text-ink-muted [&>td]:px-3 [&>td]:py-2">
      <td />
      <td>
        {kind === "untagged" ? (
          <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-ink-muted">
            untagged
          </span>
        ) : (
          <span className="font-mono tabular-nums">{network.vlan}</span>
        )}
      </td>
      <td>
        <span className="text-ink">{network.name}</span>
        <div className="text-[0.6875rem] text-ink-subtle">
          {kind === "untagged" ? "Gateway LAN · read-only here" : "On a gateway, not in the site list"}
        </div>
      </td>
      <td className="font-mono">
        {network.subnet ?? "—"}
        <div className="font-sans text-[0.6875rem] text-ink-subtle">{liveDhcpLine(network.dhcp)}</div>
      </td>
      <td className="font-mono">
        {ips.join(", ")}
        <div className="font-sans text-[0.6875rem] text-ink-subtle">
          on {network.addresses.map((a) => a.gateway).join(", ")}
        </div>
        {onVip && (
          <div className="font-sans text-[0.6875rem] text-warn">
            Still on the VIP .{vipHost}; HA needs .2/.3 on this LAN
          </div>
        )}
      </td>
      <td className="text-ink-subtle">None</td>
      <td className="text-[0.6875rem] text-ink-subtle">
        {kind === "untagged" ? "Gateway LAN — not changed by VLAN deploys" : "Not a site VLAN — left as is"}
      </td>
      <td />
    </tr>
  );
}

/* =========================================================================================
 * Per-gateway: locked WAN (live address) + live interfaces
 * ========================================================================================= */

/** One label/value pair on the WAN card; `value` null reads "Not reported yet". */
function LiveField({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex min-w-0 flex-col">
      <dt className="text-[0.6875rem] text-ink-subtle">{label}</dt>
      <dd className={cn("break-words", value ? "font-mono text-ink" : "text-ink-muted")}>{value ?? "Not reported yet"}</dd>
    </div>
  );
}

/** What the WAN is doing right now (state document), read-only, with its age. */
function WanLive({ row, now }: { row: GatewayNetworkRow; now: number }) {
  const live = row.live;
  const updated = live?.recorded ? timeAgo(new Date(live.recorded * 1000).toISOString(), now) : null;
  return (
    <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-xs">
      <LiveField label="Current address" value={live?.reported ? live.addresses.join(", ") : null} />
      {live?.gateway && <LiveField label="Default gateway" value={live.gateway} />}
      {live && live.dns.length > 0 && <LiveField label="DNS" value={live.dns.join(", ")} />}
      <div className="flex flex-col">
        <dt className="text-[0.6875rem] text-ink-subtle">Reported</dt>
        <dd className="text-ink-muted">{updated ? `Updated ${updated}` : "Not reported yet"}</dd>
      </div>
    </dl>
  );
}

function GatewayLiveSection({
  gateways,
  haPair,
  networks,
  now,
}: {
  gateways: { serial: string; name: string }[];
  haPair: HaPairView | null;
  networks: Record<string, { data?: GatewayNetworks; isLoading: boolean; error: unknown }>;
  now: number;
}) {
  const [serial, setSerial] = useState<string | null>(null);
  // Default to the first gateway (the HA primary); re-pick if the chosen one left the site.
  useEffect(() => {
    if (!serial || !gateways.some((g) => g.serial === serial)) setSerial(gateways[0]?.serial ?? null);
  }, [gateways, serial]);

  const entry = serial ? networks[serial] : undefined;
  const data = entry?.data;
  const role = serial ? gatewayRole(serial, haPair) : "single";

  if (gateways.length === 0) {
    return (
      <EmptyState
        title="No gateway at this site"
        hint="Site VLANs still go to the switches. Onboard a uCentral gateway to give the VLANs a routed interface and DHCP."
      />
    );
  }

  const all: GatewayNetworkRow[] = data ? [...data.upstream, ...data.networks] : [];

  return (
    <section className="md-card overflow-hidden" aria-label="Live on this gateway">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3">
        <div className="min-w-0 max-w-2xl">
          <h2 className="text-sm font-semibold text-ink">Live on this gateway</h2>
          <p className="mt-0.5 text-xs text-ink-muted">
            What this box is running right now. Site VLANs are added beside the untagged LAN automatically; the WAN is
            never touched.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full border border-line bg-surface-2 px-2 py-0.5 text-xs text-ink-muted">
            {ROLE_LABEL[role]}
          </span>
          <GatewayPicker serial={serial} gateways={gateways} loading={false} onChange={setSerial} />
        </div>
      </div>

      <div className="flex flex-col gap-3 p-4">
        {entry?.error != null && <ErrorBanner error={entry.error} />}
        {entry?.isLoading && <p className="text-sm text-ink-muted">Loading this gateway's configuration…</p>}
        {data && !data.editable && data.reason && (
          <NotEditableBanner title="This gateway cannot take VLAN deploys yet" reason={data.reason} />
        )}
        {/* Same no-metrics warning the deploy previews carry, with its fix (OP #1678). */}
        {data?.editable && serial && <StateReportingNotice serial={serial} metricsPresent={data.metrics_present} />}
        {data?.upstream.map((u) => (
          <LockedWanCard
            key={u.name ?? "wan"}
            name={u.name}
            summary={
              wanAddressLine(u) ??
              (u.addressing === "dynamic"
                ? "DHCP"
                : u.interface_ip
                  ? `${u.interface_ip} · ${u.subnet ?? ""}`
                  : (u.addressing ?? "—"))
            }
            ports={u.ports}
            reason={u.danger_reason}
          >
            <WanLive row={u} now={now} />
          </LockedWanCard>
        ))}

        {data && (
          <details className="rounded-md border border-line">
            <summary className="flex min-h-touch cursor-pointer items-center px-3 text-sm text-ink">
              Interfaces and addresses
              <span className="ml-2 text-xs text-ink-subtle">
                {all.length} interface{all.length === 1 ? "" : "s"} · mode {data.mode.replace("_", " ")}
              </span>
            </summary>
            <div className="overflow-x-auto border-t border-line">
              <table className="w-full min-w-[40rem] text-left text-xs">
                <thead className="text-ink-subtle">
                  <tr className="[&>th]:px-3 [&>th]:py-1.5 [&>th]:font-medium">
                    <th>Interface</th>
                    <th>VLAN</th>
                    <th>Address</th>
                    <th>Subnet</th>
                    <th>DHCP</th>
                    <th>Ports</th>
                  </tr>
                </thead>
                <tbody className="font-mono text-ink-muted">
                  {all.map((n) => (
                    <tr key={`${n.name}-${n.vlan ?? "u"}`} className="border-t border-line/60 [&>td]:px-3 [&>td]:py-1.5">
                      <td className="font-sans text-ink">
                        {n.name ?? "—"}
                        {n.danger && <span className="ml-1 font-sans text-[0.625rem] uppercase text-critical">WAN</span>}
                      </td>
                      <td className="tabular-nums">{n.vlan ?? "untagged"}</td>
                      <td>
                        {n.danger && n.live?.reported
                          ? n.live.addresses.join(", ")
                          : n.addressing === "dynamic"
                            ? "DHCP client"
                            : (n.interface_ip ?? "—")}
                      </td>
                      <td>{n.subnet ?? "—"}</td>
                      <td className="font-sans">{n.danger ? "—" : liveDhcpLine(n.dhcp)}</td>
                      <td>
                        {n.ports.join(", ") || "—"}
                        {/* A VLAN network whose ports carry no 802.1Q tag egresses UNTAGGED and
                            carries nothing, while every other column looks correct (Jedi Home,
                            2026-09-14). Say so on the row that owns the ports. */}
                        {!n.danger && n.vlan != null && n.tagged === false && (
                          <span
                            className="ml-1 font-sans text-[0.625rem] uppercase text-critical"
                            title="This VLAN is not tagged on these ports, so it leaves the gateway untagged and carries no traffic. Deploy the site VLANs to fix the tagging."
                          >
                            untagged
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="border-t border-line px-3 py-1.5 text-[0.6875rem] text-ink-subtle">
              {data.config_uuid != null ? `Config #${data.config_uuid} · ` : ""}
              LAN ports: {data.lan_ports.length ? data.lan_ports.join(", ") : "—"}
            </p>
          </details>
        )}
      </div>
    </section>
  );
}

/* =========================================================================================
 * Dialogs
 * ========================================================================================= */

/** "Deploy plan": the rollout review -- details, verify, and the manual apply path. */
function ReviewDialog({
  siteId,
  onClose,
  scopeSwitch = null,
}: {
  siteId: string;
  onClose: () => void;
  /** Re-plan only this switch (pending VLAN changes after being skipped offline). */
  scopeSwitch?: string | null;
}) {
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet flex max-h-[92vh] w-[min(96vw,64rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-lg border border-line bg-overlay shadow-sheet outline-none">
          <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-3">
            <div>
              <Dialog.Title className="text-lg font-semibold text-ink">
                {scopeSwitch ? "Re-plan switch" : "Deploy plan"}
              </Dialog.Title>
              {/* The panel below carries the mode-specific subtitle (lib/rolloutMode): a preview
                  when auto-deploy is on, the manual apply path when it is off. Keep this line
                  generic so it never contradicts that -- the old "nothing is sent unless you
                  confirm" was wrong under auto-deploy. */}
              <Dialog.Description className="mt-0.5 text-sm text-ink-muted">
                {scopeSwitch
                  ? "The site's VLANs on this one switch only (it was skipped while offline)."
                  : "What every gateway and switch gets for this site's VLANs."}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" aria-label="Close deploy plan">
                Close
              </Button>
            </Dialog.Close>
          </div>
          <div className="overflow-y-auto p-4">
            <RolloutPanel siteId={siteId} initialReviewing scopeSwitch={scopeSwitch} />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** What still uses a VLAN, from a 409 vlan_in_use body. */
function usesOf(error: unknown): { kind: string; name: string; detail: string }[] {
  if (!(error instanceof ApiError)) return [];
  const body = error.body as { uses?: { kind: string; name: string; detail: string }[] } | null;
  return Array.isArray(body?.uses) ? body!.uses! : [];
}

/**
 * Delete selected site VLANs. Deleting removes the VLAN from the site list AND queues a deploy that
 * removes it from every gateway and switch. The server refuses (409) while an SSID, port profile,
 * AP port or switch port at the site still uses the VLAN, and the dialog lists what does.
 */
function DeleteDialog({
  siteId,
  vlanIds,
  rows,
  summaries,
  onClose,
  onDeleted,
}: {
  siteId: string;
  vlanIds: number[];
  rows: SiteVlanRow[];
  summaries: Map<number, RowApplied>;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const del = useDeleteSiteVlan(siteId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const targets = rows.filter((r) => vlanIds.includes(r.vlan_id));
  const uses = usesOf(error);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      // One at a time: a refusal stops the loop, and its message names the VLAN.
      for (const r of targets) await del.mutateAsync(r.vlan_id);
      onDeleted();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && !busy && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet max-h-[90vh] w-[min(94vw,32rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">
            Delete {targets.length} site VLAN{targets.length === 1 ? "" : "s"}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Removes the VLAN from the site and, in the automatic deploy that follows, from every gateway and switch that
            carries it. Clients on it lose their network. A VLAN still used by an SSID or a port can't be deleted.
          </Dialog.Description>
          <ul className="mt-3 divide-y divide-line rounded-md border border-line text-sm">
            {targets.map((r) => {
              const onDevices = (summaries.get(r.vlan_id)?.devices ?? []).filter(
                (d) => d.state === "applied" || d.state === "tagged" || d.state === "created" || d.state === "drift",
              );
              return (
                <li key={r.vlan_id} className="px-3 py-2">
                  <span className="font-mono tabular-nums text-ink">{r.vlan_id}</span>{" "}
                  <span className="text-ink">{r.vlan_name}</span>{" "}
                  <span className="font-mono text-xs text-ink-subtle">{r.subnet ?? "L2 only"}</span>
                  <div className="text-xs text-ink-muted">
                    {onDevices.length
                      ? `Will be removed from ${onDevices.map((d) => d.name).join(", ")}`
                      : "Not on any device yet"}
                  </div>
                </li>
              );
            })}
          </ul>
          {error != null && uses.length > 0 && (
            <div role="alert" className="md-card mt-3 border-critical/40 bg-critical-soft p-3 text-sm">
              <div className="font-medium text-ink">Still in use — nothing was deleted</div>
              <ul className="mt-1 flex flex-col gap-0.5 text-ink-muted">
                {uses.map((u, i) => (
                  <li key={i}>
                    <span className="text-ink">{u.name}</span> — {u.detail}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-ink-muted">Move those to another VLAN first, then delete again.</p>
            </div>
          )}
          {error != null && uses.length === 0 && (
            <div className="mt-3">
              <ErrorBanner error={error} />
            </div>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => void run()} disabled={busy || targets.length === 0}>
              {busy ? "Deleting…" : "Delete and remove from devices"}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
